/**
 * Medals, their criteria, and the citation they are announced with.
 *
 * **The names are verbatim** from docs/game/campaign.md § *Medals*, which is
 * the manual's own Medals-and-Awards roster sorted by nation. The award
 * machinery around them is verbatim from `PS.EXE`: the citation authorities
 * `"by Imperial Decree, King George V"` / `"by Imperial Decree, Wilhelm,
 * Emperor"`, the repeat-award bar strings `"with Bar"` / `"with Two Bars"`, and
 * the `CONGRATULATIONS!!` header the award popup carries.
 *
 * **The criteria are victory thresholds**, which is the shape the game itself
 * uses: `MBPLTREC.DOC`'s `ROSTER.DAT` record documents "decorations at bytes
 * 42–50 with victory thresholds" (docs/formats/missions-campaign.md). The
 * *numbers* are port-side, anchored wherever the documents give a real one:
 *
 * - Royal Hohenzollern at 6 — Boelcke and Immelmann received it on their sixth
 *   victories (docs/game/campaign.md).
 * - The Blue Max rising with the calendar — "threshold rose from ~8 victories
 *   (1916) to 30+ (late war)" (same source), modelled as 8 / 16 / 30 by year.
 * - The Red Eagle Order at 70 — Richthofen's 70th victory, and the manual's
 *   framing of it as a one-man award, so it sits far beyond every other tier.
 * - The D.F.C. only from 1 April 1918, the day it was created with the RAF.
 *
 * The two French medals in that table are **not** in this roster: career mode
 * is RFC-or-German-only, so they cannot be earned by a player pilot.
 * `UNEARNABLE_MEDALS` records them rather than dropping them silently.
 */

import type { Nation } from "../mission/mission-roster.ts";
import { RAF_FORMED_DATE, compareDates, monthIndexOf, type CareerDate } from "./calendar.ts";

/** A victory threshold that rises as the war goes on. */
export interface MedalEscalation {
  readonly from: CareerDate;
  readonly victories: number;
}

export interface Medal {
  readonly id: string;
  /** Verbatim from docs/game/campaign.md's medal table. */
  readonly name: string;
  readonly nation: Nation;
  /** Confirmed victories for the first award. */
  readonly victories: number;
  /** Later, higher thresholds for the same medal, earliest first. */
  readonly escalation: readonly MedalEscalation[];
  /** Not awarded before this date, if the decoration did not exist yet. */
  readonly notBefore: CareerDate | undefined;
  /** How many repeat "bars" this decoration takes. `PS.EXE` knows two. */
  readonly bars: number;
  /** Further confirmed victories per bar. */
  readonly barVictories: number;
  /** What the award is worth on the career score (port-side). */
  readonly score: number;
  readonly note: string;
}

interface MedalInit {
  readonly escalation?: readonly MedalEscalation[];
  readonly notBefore?: CareerDate;
  readonly bars?: number;
  readonly barVictories?: number;
}

function medal(
  id: string,
  name: string,
  nation: Nation,
  victories: number,
  score: number,
  note: string,
  init: MedalInit = {},
): Medal {
  return {
    id,
    name,
    nation,
    victories,
    score,
    note,
    escalation: init.escalation ?? [],
    notBefore: init.notBefore,
    bars: init.bars ?? 0,
    barVictories: init.barVictories ?? 0,
  };
}

const BLUE_MAX_ESCALATION: readonly MedalEscalation[] = [
  { from: { month: monthIndexOf(1917, 0), day: 1 }, victories: 16 },
  { from: { month: monthIndexOf(1918, 0), day: 1 }, victories: 30 },
];

export const MEDALS: readonly Medal[] = [
  medal("iron-cross-2", "Iron Cross 2nd Class", "german", 1, 100, "Awarded across all services in the millions; a fighting pilot's first decoration."),
  medal("iron-cross-1", "Iron Cross 1st Class", "german", 3, 200, "Some 218,000 awarded in the war, and the step before the House Order."),
  medal("hohenzollern", "Royal Hohenzollern House Order with Swords", "german", 6, 400, "The customary step between the Iron Cross 1st Class and the Pour le Merite."),
  medal("blue-max", 'Order for Merit / "The Blue Max"', "german", 8, 900, "Prussia's highest award; 81 aviation awards in the whole war, and the bar rose every year.", {
    escalation: BLUE_MAX_ESCALATION,
  }),
  medal("red-eagle", "The Red Eagle Order", "german", 70, 1500, "Richthofen's, for his 70th victory — the manual names no other airman who received it."),
  medal("military-cross", "The Military Cross (M.C.)", "allied", 3, 200, "An Army award for captains and below; 1,077 went to R.F.C. and R.A.F. personnel.", {
    bars: 2,
    barVictories: 6,
  }),
  medal("dso", "The Distinguished Service Order (D.S.O.)", "allied", 9, 450, "Officers only, and repeat awards come as bars — Albert Ball took his with two.", {
    bars: 2,
    barVictories: 9,
  }),
  medal("dfc", "The Distinguished Flying Cross (D.F.C.)", "allied", 6, 300, "Created with the Royal Air Force on 1 April 1918, replacing the M.C. for flying men.", {
    notBefore: RAF_FORMED_DATE,
    bars: 2,
    barVictories: 8,
  }),
  medal("victoria-cross", "The Victoria Cross", "allied", 30, 1500, "The top British Empire honour, and the manual's own aspiration for an Allied career."),
];

/**
 * Medals the documents list but a career pilot cannot receive, with the reason.
 * An enumerable register rather than a silent omission.
 */
export const UNEARNABLE_MEDALS: readonly { readonly name: string; readonly reason: string }[] = [
  {
    name: "Legion of Honor",
    reason: "French. Career mode enlists only in the Royal Flying Corps or the German Air Service, so no player pilot is eligible.",
  },
  {
    name: "Croix de Guerre",
    reason: "French, and the same reason — background for the AI Escadrilles rather than a career award.",
  },
];

export function medalById(id: string): Medal | undefined {
  return MEDALS.find((entry) => entry.id === id);
}

export function medalsFor(nation: Nation): readonly Medal[] {
  return MEDALS.filter((entry) => entry.nation === nation);
}

/** The threshold in force on this date, after any wartime escalation. */
export function medalThreshold(entry: Medal, date: CareerDate): number {
  let threshold = entry.victories;
  for (const step of entry.escalation) {
    if (compareDates(date, step.from) >= 0) threshold = step.victories;
  }
  return threshold;
}

/** Verbatim `PS.EXE` bar wording. Index 0 is the plain award. */
export const BAR_SUFFIXES: readonly string[] = ["", " with Bar", " with Two Bars"];

/** Verbatim `PS.EXE` strings for the individual repeat awards. */
export const BAR_NAMES: readonly string[] = ["First Bar", "Second Bar"];

export const CITATION_AUTHORITY: Readonly<Record<Nation, string>> = {
  allied: "by Imperial Decree, King George V",
  german: "by Imperial Decree, Wilhelm, Emperor",
};

/** Verbatim `PS.EXE` award-popup header. */
export const AWARD_HEADER = "CONGRATULATIONS!!";

export function medalDisplayName(entry: Medal, bars: number): string {
  return `${entry.name}${BAR_SUFFIXES[Math.min(bars, BAR_SUFFIXES.length - 1)] ?? ""}`;
}
